import { defineStore, storeToRefs } from 'pinia';
import { useDeliveryStore } from './delivery';
import { useUserStore } from './user';
import { Variant } from '~/types/products';
import { roundToSecondDecimal } from '~/utils/QuoteHelpers';
import { AUTOSAVE_MS_DELAY, EMPTY_QUOTE, PRICE_NET, RECEIVER_COMPANY, ROT_VALUE } from '~/components/quote/consts';
import { Quote, QuoteContext, QuoteCustomItem, QuoteProduct, QuoteStatus, QuoteVariant } from '~/types/quote';
import { debounceUnique } from '~/utils/Debounce';
import gql from '@/gql/quote';
import { Option } from '~/types';

export const useQuoteStore = defineStore('quote', () => {
  const isQuoteEditMode = ref<boolean>(false);
  const quoteContext = ref('normal');

  const quoteItems = ref<QuoteVariant[]>([]);
  const constantMarkup = ref<number>(0);
  const constantDiscount = ref<number>(0);
  const { $quoteService, $i18n } = useNuxtApp();

  // For default values, the priority will be:
  // 1. Value from the saved quote, if editing.
  // 2. Value from the previous quote, if creating a next one.
  // 3. Value from the user profile

  const quote = ref<Quote>({
    status: QuoteStatus.draft,
    finalMessage: '',
    validFrom: '',
    validFor: 0,
    sender: {
      name: '',
      organizationNumber: '', // This will be a default value after the customer saves at least once
      reference: '',
      address: {
        address: ``,
        postalCode: ``,
        postalAddress: ``,
      },
    },
    receiver: {
      type: RECEIVER_COMPANY,
      label: '',
      name: '',
      contactPerson: '',
      address: {
        address: '',
        postalCode: '',
        postalAddress: '',
      },
    },
    priceType: PRICE_NET,
    items: [],
    mergeArticles: false,
    showDiscounts: false,
    customItems: [],
  });

  const statuses: Option[] = [
    { value: '', label: $i18n.t('all') },
    { value: QuoteStatus.draft, label: $i18n.t(`quote.status.draft`) },
    { value: QuoteStatus.accepted, label: $i18n.t(`quote.status.accepted`) },

    // TODO use this after we decide which statuses should the quote have
    // ...Object.entries(QuoteStatus).map<Option>(([, value]: [string, string]) => ({
    //   value,
    //   label: $i18n.t(`quote.status.${value}`),
    // })),
  ];

  const setQuoteEditMode = (enable: boolean) => {
    isQuoteEditMode.value = enable;
  };

  const setQuoteContext = (value: QuoteContext) => {
    quoteContext.value = value;
  };

  const initNewQuote = async (): Promise<number> => {
    const { data } = await $quoteService.createEmptyQuote();
    quote.value = {
      ...EMPTY_QUOTE,
      ...(data as Quote),
      status: QuoteStatus.draft,
    };
    quoteItems.value = [];
    return quote.value.quoteId!;
  };

  const getQuoteById = async (quoteId: string): Promise<Quote | null> => {
    const { data } = await $quoteService.getQuoteById(quoteId);
    if (!data) return null; // handle error
    quote.value = data;
    quoteItems.value = [];
    if (quote.value.items.length > 0) {
      const { $apiGateway } = useNuxtApp();
      const { getSelectedDeliveryId, getStockInput } = storeToRefs(useDeliveryStore());
      const { getPreferredStore } = storeToRefs(useUserStore());

      // call graphql
      const articleNumbers = quote.value.items.map((item) => item.articleNumber);
      const { data, error } = await $apiGateway.post('/graphql', {
        query: gql.getVariantsForQuote,
        variables: {
          articleNumbers,
          deliveryId: getSelectedDeliveryId.value,
          stockInput: getStockInput.value,
          skipStock: true,
          skipPrice: false,
          locationId: getPreferredStore.value.id,
        },
      });

      for (const item of quote.value.items) {
        // TODO - what should happen if the variant got deleted from PIM?
        const variant = data.data.variants.find((variant: Variant) => variant?.articleNumber === item.articleNumber);
        const quoteVariant: QuoteVariant = {
          ...variant,
          ...item,
          isSelected: false,
        };

        if (!variant) {
          quoteVariant.name = item.articleName;
          quoteVariant.discontinued = true;
        }

        quoteItems.value.push(quoteVariant);
      }

      if (error) quoteItems.value = [];
    }

    return data;
  };

  const patchQuote = debounceUnique(async (param: string) => {
    // Should never really happen
    if (!quote.value.quoteId) return;

    const payload: Record<string, any> = {};
    payload[param] = quote.value[param as keyof Quote];
    await $quoteService.updateQuote(quote.value.quoteId, payload);
  }, AUTOSAVE_MS_DELAY);

  const addEmptyCustomItem = async () => {
    const customItem = {
      isRot: false,
    };

    const { data } = await $quoteService.addCustomItem(quote.value.quoteId!, customItem);
    if (!data) return; // TODO handle error here

    quote.value.customItems.push({ ...customItem, isSelected: false, quoteCustomItemId: data.quoteCustomItemId });
  };

  const patchCustomItem = debounceUnique(async (customItem: QuoteCustomItem) => {
    if (!quote.value.quoteId) return;

    const payload = {
      quoteCustomItemId: customItem.quoteCustomItemId,
      customItemName: customItem.customItemName ?? undefined,
      vatRate: customItem.vatRate ?? undefined,
      isRot: customItem.isRot,
      quantity: customItem.quantity ?? undefined,
      price: customItem.price ?? undefined,
      unit: customItem.unit ?? undefined,
    };

    await $quoteService.updateCustomItem(quote.value.quoteId, [payload]);
  }, AUTOSAVE_MS_DELAY);

  const deleteSelectedCustomItems = async () => {
    const selectedItemIds = quote.value.customItems
      .filter((item) => item.isSelected)
      .map((item) => item.quoteCustomItemId);
    const { error } = await $quoteService.deleteCustomItems(quote.value.quoteId!, selectedItemIds);
    if (error) return; // TODO add error handling

    quote.value!.customItems = quote.value.customItems.filter((item) => !item.isSelected);
  };

  const addItemToQuote = async (variant: Variant, quantity: number) => {
    const netPrice = parseFloat(variant.price?.netPrice ?? '0');
    const salesPrice = parseFloat(variant.price?.salesPrice ?? '0');
    const payload = {
      articleNumber: variant.articleNumber!,
      quantity,
      markup: constantMarkup.value || 0,
      salesDiscount: constantDiscount.value || 0,
      quoteNetPrice: constantMarkup.value
        ? roundToSecondDecimal(netPrice + (netPrice * constantMarkup.value) / 100)
        : netPrice,
      quoteSalesPrice: constantDiscount.value
        ? roundToSecondDecimal(salesPrice - (salesPrice / 100) * constantDiscount.value)
        : salesPrice,
      netPrice,
      salesPrice,
      articleName: variant.name,
    };

    const { data, error } = await $quoteService.addQuoteItem(quote.value.quoteId!, payload);

    if (!data || error) return;

    const quoteVariant: QuoteVariant = {
      ...variant,
      ...data,
      isSelected: false,
    };
    quoteItems.value.push(quoteVariant);
  };

  const getPatchQuotePayload = (quoteVariant: QuoteVariant): QuoteProduct => {
    return {
      articleNumber: quoteVariant.articleNumber,
      netPrice: quoteVariant.netPrice,
      quantity: quoteVariant.quantity,
      quoteNetPrice: quoteVariant.quoteNetPrice,
      quoteSalesPrice: quoteVariant.quoteSalesPrice,
      salesPrice: quoteVariant.salesPrice,
      markup: quoteVariant.markup ?? 0,
      salesDiscount: quoteVariant.salesDiscount,
      quoteItemId: quoteVariant.quoteItemId,
    };
  };

  const patchQuoteItem = debounceUnique(async (quoteVariant: QuoteVariant) => {
    const payload = getPatchQuotePayload(quoteVariant);

    await $quoteService.updateQuoteItems(quote.value.quoteId!, [payload]);
  }, AUTOSAVE_MS_DELAY);

  const patchAllQuoteItems = debounceUnique(async () => {
    const payloads: QuoteProduct[] = [];
    for (const item of quoteItems.value) {
      const payload = getPatchQuotePayload(item);
      payloads.push(payload);
    }

    await $quoteService.updateQuoteItems(quote.value.quoteId!, payloads);
  }, AUTOSAVE_MS_DELAY);

  const deleteSelectedQuoteItems = async () => {
    const selectedItemIds = quoteItems.value.filter((item) => item.isSelected).map((item) => item.quoteItemId);
    const { error } = await $quoteService.deleteQuoteItems(quote.value.quoteId!, selectedItemIds);
    if (error) return;

    quoteItems.value = quoteItems.value.filter((item) => !item.isSelected);
  };

  const isQuoteInExternalMode = computed(() => isQuoteEditMode.value && quoteContext.value === 'external');

  const isNet = computed((): boolean => {
    return quote.value?.priceType === PRICE_NET;
  });

  // PRODUCTS
  const sumMarkup = computed((): number => {
    return quoteItems.value.reduce((sum, item) => (sum += (item.quoteNetPrice - item.netPrice) * item.quantity), 0);
  });
  const sumProductsWithoutVat = computed((): number => {
    if (isNet.value) {
      return quoteItems.value.reduce((sum, item) => (sum += item.quoteNetPrice * item.quantity), 0);
    } else {
      return quoteItems.value.reduce((sum, item) => (sum += item.quoteSalesPrice * item.quantity), 0);
    }
  });
  const sumProductsVat = computed((): number => {
    return sumProductsWithoutVat.value * 0.25;
  });
  const sumProductsToPay = computed((): number => {
    return sumProductsWithoutVat.value + sumProductsVat.value;
  });
  const sumNetPrice = computed((): number => {
    return quoteItems.value.reduce((sum, item) => (sum += item.netPrice * item.quantity), 0);
  });
  const sumSalesDiscount = computed((): number => {
    return quoteItems.value.reduce((sum, item) => (sum += (item.salesPrice - item.quoteSalesPrice) * item.quantity), 0);
  });

  // SERVICES
  const sumServicesVat = computed((): number => {
    return quote.value!.customItems.reduce(
      (sum, item) => (sum += ((item.price ?? 0) * (item.vatRate ?? 0) * (item.quantity ?? 1)) / 100),
      0,
    );
  });
  const sumServicesPrice = computed((): number => {
    return quote.value!.customItems.reduce((sum, item) => (sum += (item.price ?? 0) * (item.quantity ?? 1)), 0);
  });
  const sumROT = computed((): number => {
    let sum = 0;
    for (const item of quote.value!.customItems) {
      if (!item.isRot) {
        sum += 0;
      } else {
        sum += (((item.price ?? 0) * (item.vatRate ?? 0)) / 100 + (item.price ?? 0)) * (item.quantity ?? 1) * ROT_VALUE;
      }
    }
    return sum;
  });
  const sumServicesToPay = computed((): number => {
    return sumServicesVat.value + sumServicesPrice.value - sumROT.value;
  });

  // TOTAL
  const sumVat = computed((): number => {
    return sumProductsVat.value + sumServicesVat.value;
  });
  const sumWithoutVat = computed((): number => {
    return sumProductsWithoutVat.value + sumServicesPrice.value;
  });
  const sumToPay = computed((): number => {
    return sumWithoutVat.value + sumVat.value - sumROT.value;
  });

  return {
    quote,
    statuses,
    isQuoteEditMode,
    setQuoteEditMode,
    quoteItems,
    addItemToQuote,
    constantMarkup,
    constantDiscount,
    quoteContext,
    isQuoteInExternalMode,
    setQuoteContext,
    isNet,
    sumMarkup,
    sumProductsWithoutVat,
    sumProductsVat,
    sumProductsToPay,
    sumNetPrice,
    sumSalesDiscount,
    sumServicesVat,
    sumServicesPrice,
    sumROT,
    sumServicesToPay,
    sumVat,
    sumWithoutVat,
    sumToPay,
    initNewQuote,
    patchQuote,
    getQuoteById,
    addEmptyCustomItem,
    patchCustomItem,
    deleteSelectedCustomItems,
    patchQuoteItem,
    deleteSelectedQuoteItems,
    patchAllQuoteItems,
  };
});
